// @flow
import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { getSearchUrlWithParams, getSellerUrl, getSimilarItemsUrl } from '../../utils/urls';
import { type CategoryLink } from '../../types/CategoryLink';
import classNames from 'classnames';
import isEqual from 'react-fast-compare';
import type { ItemCard } from '../../types/ItemCard';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import PageTitle from '../../components/PageTitle/PageTitle';
import PriceResults from '../../components/PriceResults/PriceResults';
import SaveSearch from '../../components/SaveSearch/SaveSearch';
import { type State as SaveSearchState } from '../../redux/modules/saveSearch';
import SimilarItemsContainer from '../SimilarItems/SimilarItemsContainer';
import styles from './ZeroResults.module.scss';

const generateSuggestionLinks = (suggestion: any, house: any) => {
    let suggestionLink;
    if (suggestion) {
        suggestionLink = (
            <>
                “
                <Link key="suggestion-link" route to={getSimilarItemsUrl(suggestion.value)}>
                    {suggestion.label}
                </Link>
                ”
            </>
        );
    }

    let houseLink;
    if (house) {
        houseLink = (
            <Link key="house-link" route to={getSellerUrl(house.houseId, house.value)}>
                {house.value}
            </Link>
        );
    }

    if (suggestionLink && houseLink) {
        return <FormattedMessage id="searchResults.or" values={{ houseLink, suggestionLink }} />;
    }

    return suggestionLink || houseLink;
};

type Props = $ReadOnly<{|
    categoryLinks: CategoryLink[],
    categoryPage?: boolean,
    className: string,
    creator?: any,
    deleteSavedSearch: Function,
    fetchItemsIfNeeded: Function,
    houses: any[],
    isLoggedIn: boolean,
    saveSearchCheckState: any,
    saveSearchState: SaveSearchState,
    searchSubmitted: boolean,
    searchTerm: string,
    seeResultsRoute?: string,
    soldItems?: ItemCard[],
    soldItemsFound?: number,
    submitSaveSearch: Function,
    suggestions: any[],
    tooltipMessage: React.Node,
    trackZeroSearchResultsShelfClick: Function,
    width: number,
|}>;

const ZeroResults = ({
    categoryLinks,
    categoryPage,
    className = '',
    creator,
    deleteSavedSearch,
    fetchItemsIfNeeded,
    houses,
    isLoggedIn,
    saveSearchCheckState,
    saveSearchState,
    searchSubmitted,
    searchTerm,
    seeResultsRoute = '',
    soldItems,
    soldItemsFound,
    submitSaveSearch,
    suggestions,
    tooltipMessage,
    trackZeroSearchResultsShelfClick,
    width,
}: Props) => {
    if (!searchTerm) return null;
    const itemId = soldItems?.[0]?.item?.itemId;
    const firstSuggestion = suggestions?.[0];
    const firstHouse = houses?.[0];
    const hasSuggestions = Boolean(firstSuggestion) || Boolean(firstHouse);

    const classes = classNames(styles.zeroResults, className);
    const titleClasses = classNames(styles.title, { [styles.hasSuggestion]: hasSuggestions });
    const isCreatorPage = Boolean(creator?.name);
    const modifiedSearchTerm = categoryPage ? searchTerm : `“${searchTerm}”`;

    return (
        <div className={classes}>
            {categoryPage ? (
                <>
                    <PageTitle className={styles['search-term']} title={searchTerm} />
                    <span className={styles['results-count']}>
                        (
                        <FormattedMessage id="search_item_count_items" values={{ count: 0 }} />)
                    </span>
                </>
            ) : (
                <PageTitle
                    className={titleClasses}
                    title={
                        <FormattedMessage
                            id="searchResults.noResultsTitle"
                            values={{
                                searchTerm,
                            }}
                        />
                    }
                />
            )}
            {hasSuggestions && !categoryPage && (
                <div className={styles.suggestion}>
                    <FormattedMessage
                        id="searchResults.lookingFor"
                        values={{
                            suggestion: generateSuggestionLinks(firstSuggestion, firstHouse),
                        }}
                    />
                </div>
            )}
            <div className={classNames(styles.top, { [styles.categoryPage]: categoryPage })}>
                <div className={styles.left}>
                    <div className={styles.subTitle}>
                        <FormattedMessage id="searchResults.noResultsSubtitle" />
                    </div>
                    <div className={styles.subSubTitle}>
                        <FormattedMessage
                            id="searchResults.noResultsSubSubtitle1"
                            values={{
                                count: (
                                    <FormattedNumber
                                        value={soldItemsFound}
                                        maximumFractionDigits={0}
                                        minimumFractionDigits={0}
                                    />
                                ),
                                searchTerm: <span className={styles.subTitleTerm}>{modifiedSearchTerm}</span>,
                            }}
                        />
                        <br />
                        <FormattedMessage id="searchResults.noResultsSubSubtitle2" />
                    </div>
                    <SaveSearch
                        deleteSavedSearch={deleteSavedSearch}
                        primary
                        saveSearchCheckState={saveSearchCheckState}
                        saveSearchState={saveSearchState}
                        submitSaveSearch={() => {
                            submitSaveSearch({ keyword: searchTerm, status: 'online' }, true);
                        }}
                        tooltipMessage={tooltipMessage}
                    />
                </div>
                <div className={styles.right}>
                    {Boolean(soldItems?.length) && (
                        <PriceResults
                            count={soldItemsFound || 0}
                            creator={creator?.name && searchTerm}
                            isLoggedIn={isLoggedIn}
                            items={soldItems}
                            seeAllUrl={
                                isCreatorPage
                                    ? seeResultsRoute
                                    : getSearchUrlWithParams({
                                          keyword: searchTerm,
                                          page: 1,
                                          pageSize: 24,
                                          sort: '-relevance',
                                          status: 'archive',
                                      })
                            }
                        />
                    )}
                </div>
            </div>
            <div className={styles.shelves}>
                {itemId && (
                    <SimilarItemsContainer
                        itemId={itemId}
                        suggestions={suggestions}
                        categoryLinks={categoryLinks}
                        title={
                            <FormattedMessage
                                id="searchResults.relatedTo"
                                values={{ searchTerm: modifiedSearchTerm }}
                            />
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo<Props>(ZeroResults, isEqual);
